import { chatbotHttp as api } from "./chatbotApi";
import { searchCustomers } from "./schedulingApi";

const base = (botId) => `/bots/${encodeURIComponent(botId)}/forms`;
const unwrap = (response) => response.data?.data;

export const getFormsErrorMessage = (error, fallback = "Something went wrong.") => {
  const body = error?.response?.data;
  if (body?.message && body.message !== "ok") return body.message;
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      const field = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== "body").join(".") : "";
      return field ? `${field}: ${item?.msg || "Invalid value"}` : item?.msg || "Invalid value";
    }).join("; ");
  }
  return error?.message || fallback;
};

export const listFormTemplates = (botId, params = {}) => api.get(`${base(botId)}/templates`, { params: { active: true, ...params } }).then(unwrap);
export const getFormTemplate = (botId, templateId, params = {}) => api.get(`${base(botId)}/templates/${templateId}`, { params }).then(unwrap);
export const createFormTemplate = (botId, values) => api.post(`${base(botId)}/templates`, values).then(unwrap);
export const updateFormTemplate = (botId, templateId, values) => api.patch(`${base(botId)}/templates/${templateId}`, values).then(unwrap);
export const createFormVersion = (botId, templateId, values) => api.post(`${base(botId)}/templates/${templateId}/versions`, values).then(unwrap);
export const updateFormVersion = (botId, templateId, versionId, values) => api.patch(`${base(botId)}/templates/${templateId}/versions/${versionId}`, values).then(unwrap);
export const publishFormVersion = (botId, templateId, versionId) => api.post(`${base(botId)}/templates/${templateId}/versions/${versionId}/publish`).then(unwrap);
export const deleteFormVersion = (botId, templateId, versionId) => api.delete(`${base(botId)}/templates/${templateId}/versions/${versionId}`).then(unwrap);
export const listFormRequests = (botId, params = {}) => api.get(`${base(botId)}/requests`, { params }).then(unwrap);
export const createFormRequest = (botId, values) => api.post(`${base(botId)}/requests`, values).then(unwrap);
export const updateFormRequest = (botId, requestId, values) => api.patch(`${base(botId)}/requests/${requestId}`, values).then(unwrap);
export const previewNewFormRequest = (botId, values) => api.post(`${base(botId)}/requests/preview`, values).then(unwrap);
export const previewFormRequest = (botId, requestId) => api.post(`${base(botId)}/requests/${requestId}/preview`).then(unwrap);
export const sendFormRequest = (botId, requestId, values) => api.post(`${base(botId)}/requests/${requestId}/send`, values).then(unwrap);
// Non-secret de-duplication key; one per resend click so retried HTTP calls are not sent twice.
export const newIdempotencyKey = () => (
  typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `resend-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
);
export const resendFormRequest = (botId, requestId, delivery = "link", idempotencyKey) => api.post(
  `${base(botId)}/requests/${requestId}/resend`,
  delivery === "whatsapp" ? { delivery, idempotency_key: idempotencyKey || newIdempotencyKey() } : { delivery },
).then(unwrap);
export const cancelFormRequest = (botId, requestId, revision, reason) => api.post(`${base(botId)}/requests/${requestId}/cancel`, { revision, reason }).then(unwrap);
export const listFormComments = (botId, requestId) => api.get(`${base(botId)}/requests/${requestId}/comments`).then(unwrap);
export const addFormComment = (botId, requestId, comment) => api.post(`${base(botId)}/requests/${requestId}/comments`, { comment }).then(unwrap);
export const listFormActivity = (botId, requestId) => api.get(`${base(botId)}/requests/${requestId}/activity`).then(unwrap);
export const listFormEmails = (botId, requestId, params) => {
  const path = `${base(botId)}/requests/${requestId}/emails`;
  return (params ? api.get(path, { params }) : api.get(path)).then(unwrap);
};
export const resendFormEmail = (botId, requestId, idempotencyKey = newIdempotencyKey()) => api.post(
  `${base(botId)}/requests/${requestId}/emails/resend`,
  { idempotency_key: idempotencyKey },
).then(unwrap);
export const getFormDocument = (botId, requestId, inline = false) => (
  inline ? api.get(`${base(botId)}/requests/${requestId}/document`, { params: { inline: true } })
    : api.get(`${base(botId)}/requests/${requestId}/document`)
).then(unwrap);
export const searchFormCustomers = (botId, query) => searchCustomers(botId, { q: query || undefined });

const publicPath = (token) => `/public/forms/${encodeURIComponent(token)}`;
const proofHeaders = (proof) => proof ? { headers: { "X-Form-Verification": proof } } : {};

export const openPublicForm = (token, proof) => api.get(publicPath(token), proofHeaders(proof)).then(unwrap);
export const sendFormOtp = (token) => api.post(`${publicPath(token)}/otp/send`).then(unwrap);
export const verifyFormOtp = (token, code) => api.post(`${publicPath(token)}/otp/verify`, { code }).then(unwrap);
export const submitPublicForm = (token, values, proof) => api.post(`${publicPath(token)}/submit`, values, proofHeaders(proof)).then(unwrap);
export const getPublicFormDocument = (token, proof) => api.get(`${publicPath(token)}/document`, proofHeaders(proof)).then(unwrap);